import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import Wordmark from '../components/Wordmark';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        password,
        confirm_password: confirm
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Could not reset password. The link may have expired.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <Wordmark />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          <h1
            className="text-3xl sm:text-4xl font-normal mb-3 leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Reset your password
          </h1>

          {success ? (
            <div
              data-testid="reset-password-success-message"
              className="bg-[#0a5c44]/10 border border-[#0a5c44]/20 text-[#0a5c44] rounded-lg p-6"
            >
              Password reset successfully. Redirecting you to sign in...
            </div>
          ) : (
            <>
              <p className="text-[#1a1a1a]/70 mb-8">
                Enter your new password below.
              </p>

              {error && (
                <div
                  data-testid="reset-password-error-message"
                  className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-base font-normal text-[#1a1a1a]">
                    New password
                  </Label>
                  <PasswordInput
                    id="password"
                    data-testid="reset-password-password-input"
                    testIdToggle="reset-password-password-toggle"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white border-[#e0ddd8] mt-2"
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                  />
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-base font-normal text-[#1a1a1a]">
                    Confirm password
                  </Label>
                  <PasswordInput
                    id="confirm"
                    data-testid="reset-password-confirm-input"
                    testIdToggle="reset-password-confirm-toggle"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="bg-white border-[#e0ddd8] mt-2"
                    placeholder="Re-enter new password"
                    required
                    minLength={8}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="reset-password-submit-button"
                  disabled={loading}
                  className="w-full bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors h-11"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
